/**
 * The simple product gallery script.
 *
 * A simple product gallery just switches the images on thumbnail select.
 *
 * ## Interaction
 * - A click can happen on the product-gallery__thumbnail-wrapper element.
 * This has the [aria-current] attribute with the 0-based index of the thumbnail.
 *
 * The image that needs to become visible will have a class of `product-gallery__image--{index0}`.
 *
 * ## A11y
 * - Ensure the aria-current is false when the thumbnail is not selected.
 * - Ensure the aria-current is true when the thumbnail is selected.
 * - Ensure only one thumbnail can be selected at a time.
 */

import { onClick } from "@nx-paeonia/utils-events";
import { createStore } from "zustand/vanilla";

interface ProductGalleryState {
	currentIndex: number;
}

/** Formats the index to a class that can be used to target the main image. */
const classFmt = (index: number) => `product-gallery__image--${index}`;
const state = createStore<ProductGalleryState>(() => ({
	currentIndex: 0,
}));

state.subscribe(({ currentIndex }) => {
	const imageClass = classFmt(currentIndex);
	const thumbnails = document.querySelectorAll<HTMLElement>(`.product-gallery__thumbnail-wrapper`);

	thumbnails.forEach((el) => {
		el.setAttribute("aria-current", "false");
	});

	wrapperElement?.querySelectorAll(".product-gallery__image").forEach((img) => {
		img.classList.remove("is-visible");
		img.setAttribute("aria-hidden", "true");
	});

	const targetElement = wrapperElement?.querySelector<HTMLElement>(`.${imageClass}`);

	if (!targetElement) return;

	targetElement.classList.add("is-visible");
	targetElement.removeAttribute("aria-hidden");
	Array.from(thumbnails)
		.find((el) => el.dataset.index === `${currentIndex}`)
		?.setAttribute("aria-current", "true");
});

const wrapperElement = document.querySelector<HTMLElement>(".product-gallery");

onClick(".product-gallery__thumbnail-wrapper", (_, element) => {
	state.setState({ currentIndex: Number(element.dataset.index) });
});
